import { useState, useRef, useCallback } from 'react';
import { TextInput } from 'react-native';
import { sanitizeCurrencyInput } from '@/utils/common/formatting';

interface UseAmountInputOptions {
  onAmountChange?: (amount: string) => void;
}

interface UseAmountInputReturn {
  amountType: 'balance' | 'other';
  customAmount: string;
  inputRef: React.RefObject<TextInput | null>;
  selectBalance: () => void;
  selectOther: () => void;
  handleAmountChange: (text: string) => void;
  /** Set the amount type directly (for initializing from saved data) */
  setAmountType: React.Dispatch<React.SetStateAction<'balance' | 'other'>>;
  /** Set the custom amount directly (for initializing from saved data) */
  setCustomAmount: React.Dispatch<React.SetStateAction<string>>;
  reset: () => void;
}

/**
 * Shared hook for "balance vs other amount" input logic.
 * Manages amount type state, validated custom amount input,
 * and auto-focus behavior when switching to "other".
 */
export const useAmountInput = (
  options?: UseAmountInputOptions,
): UseAmountInputReturn => {
  const [amountType, setAmountType] = useState<'balance' | 'other'>('balance');
  const [customAmount, setCustomAmount] = useState('');
  const inputRef = useRef<TextInput>(null);

  const selectBalance = useCallback(() => {
    setAmountType('balance');
    options?.onAmountChange?.('');
  }, [options?.onAmountChange]);

  const selectOther = useCallback(() => {
    setAmountType('other');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleAmountChange = useCallback((text: string) => {
    const cleaned = sanitizeCurrencyInput(text);
    setCustomAmount(cleaned);
    options?.onAmountChange?.(cleaned);
  }, [options?.onAmountChange]);

  const reset = useCallback(() => {
    setAmountType('balance');
    setCustomAmount('');
  }, []);

  return {
    amountType,
    customAmount,
    inputRef,
    selectBalance,
    selectOther,
    handleAmountChange,
    setAmountType,
    setCustomAmount,
    reset,
  };
};
